import React, { useEffect, useState, useRef } from 'react';
import { XIcon, CalendarIcon, ClockIcon, UserIcon, MusicIcon, MapPinIcon, FileTextIcon, PlusIcon, SearchIcon, ChevronDownIcon, RepeatIcon } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, writeBatch, doc as firestoreDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { CustomStudentDropdown } from './CustomStudentDropdown';
import { CustomDropdown } from './CustomDropdown';
import { getTeacherStudents } from '../utils/teacherUtils';
import { AddStudentModal } from './modals/AddStudentModal';
import { toast } from 'react-hot-toast';
import { generateDurationOptions } from '../utils/timeUtils';
import { StudentPicker } from './StudentPicker';
import { TimePickerDropdown } from './TimePickerDropdown';
import { DurationPickerDropdown } from './DurationPickerDropdown';
import { createLesson } from '../src/services/lessonsService';
import type { CreateLessonInput } from '../src/types/lesson';
interface CreateNewLessonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLessonAdded: (lesson: any) => void;
  darkMode?: boolean;
  initialDate?: string;
  initialTime?: string;
  initialStudentId?: string;
  teacherDefaultSettings?: {
    duration: number;
    location: string;
    notes: string;
  } | null;
}
export const CreateNewLessonModal: React.FC<CreateNewLessonModalProps> = ({
  isOpen,
  onClose,
  onLessonAdded,
  darkMode = false,
  initialDate = '',
  initialTime = '',
  initialStudentId = '',
  teacherDefaultSettings = null
}) => {
  const {
    currentUser
  } = useAuth();
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  // Dragging state - OPTIMIZED: Use refs for drag offset to avoid re-renders
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({
    x: 0,
    y: 0
  });
  const rafIdRef = useRef<number | null>(null);
  const [dragStartPos, setDragStartPos] = useState({
    x: 0,
    y: 0
  });
  const [modalPosition, setModalPosition] = useState({
    top: 0,
    left: 0
  });
  const [isMobile, setIsMobile] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // Instrument autosuggest state
  const [isInstrumentDropdownOpen, setIsInstrumentDropdownOpen] = useState(false);
  const [filteredInstruments, setFilteredInstruments] = useState<string[]>([]);
  const [selectedInstrumentIndex, setSelectedInstrumentIndex] = useState(-1);
  const instrumentDropdownRef = useRef<HTMLDivElement>(null);
  const instrumentInputRef = useRef<HTMLInputElement>(null);
  const formContentRef = useRef<HTMLDivElement>(null);
  const [justSelectedInstrument, setJustSelectedInstrument] = useState(false);
  // Form state - CRITICAL: Initialize with a flag to track if defaults have been applied
  const [lessonForm, setLessonForm] = useState({
    title: '',
    studentId: initialStudentId,
    date: initialDate || new Date().toISOString().split('T')[0],
    preciseStartTime: initialTime || '09:00',
    duration: 60,
    location: 'Online',
    instrument: '',
    notes: '',
    status: 'confirmed'
  });
  // Track if we've applied defaults to prevent re-applying
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  // NEW: Recurring lesson state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState<number[]>([]);
  const [recurringEndDate, setRecurringEndDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 28); // Default 4 weeks
    return date.toISOString().split('T')[0];
  });
  // Calculate recurring count
  const calculateRecurringCount = () => {
    if (!isRecurring || recurringDays.length === 0 || !recurringEndDate) return 0;
    let count = 0;
    const today = new Date(lessonForm.date);
    today.setHours(0, 0, 0, 0);
    const end = new Date(recurringEndDate + 'T23:59:59');
    const cursor = new Date(today);
    while (cursor <= end) {
      if (recurringDays.includes(cursor.getDay())) {
        count++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return count;
  };
  // CRITICAL FIX: Reset ALL form fields when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('[CreateNewLessonModal] 🔄 Resetting form on modal open');
      const initialDateValue = initialDate || new Date().toISOString().split('T')[0];
      // Reset ALL fields to clean state
      setLessonForm({
        title: '',
        studentId: '',
        date: initialDateValue,
        preciseStartTime: initialTime || '09:00',
        duration: 60,
        location: 'Online',
        instrument: '',
        notes: '',
        status: 'confirmed'
      });
      // Reset defaults applied flag so teacher settings can be reapplied
      setDefaultsApplied(false);
      // Clear any validation errors
      setTimeValidationError('');
      setError('');
      // Reset recurring state
      setIsRecurring(false);
      setRecurringDays([]);
      const date = new Date();
      date.setDate(date.getDate() + 28);
      setRecurringEndDate(date.toISOString().split('T')[0]);
    }
  }, [isOpen, initialDate, initialTime]);
  // NEW: Update date when initialDate prop changes
  useEffect(() => {
    if (isOpen && initialDate) {
      console.log('[CreateNewLessonModal] Updating date from initialDate:', initialDate);
      setLessonForm(prev => ({
        ...prev,
        date: initialDate
      }));
    }
  }, [isOpen, initialDate]);
  // NEW: Add time validation error state
  const [timeValidationError, setTimeValidationError] = useState('');
  // DIAGNOSTIC: Log when modal opens and receives settings
  useEffect(() => {
    if (isOpen) {
      console.log('[CreateNewLessonModal] 🚪 Modal opened');
      console.log('[CreateNewLessonModal] 📦 Received teacherDefaultSettings:', {
        settings: teacherDefaultSettings,
        hasDuration: teacherDefaultSettings?.duration !== undefined,
        durationValue: teacherDefaultSettings?.duration,
        hasLocation: teacherDefaultSettings?.location !== undefined,
        locationValue: teacherDefaultSettings?.location
      });
      console.log('[CreateNewLessonModal] 📋 Current lessonForm.duration:', lessonForm.duration);
    }
  }, [isOpen, teacherDefaultSettings, lessonForm.duration]);
  // Apply default settings when modal opens - CRITICAL FIX
  useEffect(() => {
    if (isOpen && teacherDefaultSettings && !defaultsApplied) {
      console.log('[CreateNewLessonModal] ✅ Applying teacher default settings:', {
        duration: teacherDefaultSettings.duration,
        location: teacherDefaultSettings.location,
        notes: teacherDefaultSettings.notes
      });
      setLessonForm(prev => {
        const updated = {
          ...prev,
          duration: teacherDefaultSettings.duration,
          location: teacherDefaultSettings.location,
          notes: teacherDefaultSettings.notes
        };
        console.log('[CreateNewLessonModal] 📝 Updated lessonForm:', {
          oldDuration: prev.duration,
          newDuration: updated.duration,
          oldLocation: prev.location,
          newLocation: updated.location
        });
        return updated;
      });
      setDefaultsApplied(true);
    } else if (isOpen && !teacherDefaultSettings) {
      console.log('[CreateNewLessonModal] ⚠️ No teacher default settings provided');
      console.log('[CreateNewLessonModal] 📋 Using hardcoded defaults: duration=60, location=Online');
    }
  }, [isOpen, teacherDefaultSettings, defaultsApplied]);
  // Reset defaults applied flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDefaultsApplied(false);
    }
  }, [isOpen]);
  // Comprehensive instrument list
  const allInstruments = [
  // Classical instruments
  'Piano', 'Violin', 'Viola', 'Cello', 'Double Bass', 'Flute', 'Clarinet', 'Oboe', 'Bassoon', 'Trumpet', 'Trombone', 'French Horn', 'Tuba', 'Harp', 'Percussion',
  // Modern/pop instruments
  'Guitar', 'Bass Guitar', 'Drums', 'Voice', 'Saxophone', 'Ukulele', 'Synthesizer', 'Keyboard',
  // Production & composition
  'Composition', 'Arranging', 'Orchestration', 'Songwriting', 'Music Theory', 'Ear Training',
  // DAWs & tech
  'Ableton Live', 'Logic Pro', 'FL Studio', 'Cubase', 'Pro Tools', 'Studio One', 'GarageBand', 'Reaper',
  // Music business & career
  'Music Business', 'Publishing', 'Copyright', 'Recording', 'Mixing', 'Mastering', 'Sound Design', 'Film Scoring'];
  // Location options for the dropdown
  const locationOptions = [{
    value: 'Online',
    label: 'Online'
  }, {
    value: 'In-Person',
    label: 'In-Person'
  }, {
    value: "Student's Home",
    label: "Student's Home"
  }, {
    value: "Teacher's Studio",
    label: "Teacher's Studio"
  }];
  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Center modal on open
  useEffect(() => {
    if (isOpen && !isMobile) {
      const centerModal = () => {
        if (!modalRef.current) return;
        // Get actual modal dimensions
        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        // Calculate centered position
        const left = (viewportWidth - modalWidth) / 2;
        const top = (viewportHeight - modalHeight) / 2;
        // Ensure minimum padding from edges (20px)
        const minPadding = 20;
        const finalLeft = Math.max(minPadding, Math.min(left, viewportWidth - modalWidth - minPadding));
        const finalTop = Math.max(minPadding, Math.min(top, viewportHeight - modalHeight - minPadding));
        setModalPosition({
          top: finalTop,
          left: finalLeft
        });
        // Reset drag offset on every open
        dragOffsetRef.current = {
          x: 0,
          y: 0
        };
      };
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(centerModal);
    }
  }, [isOpen, isMobile]);
  // Load students when the modal opens
  useEffect(() => {
    if (isOpen && currentUser) {
      loadStudents();
    }
  }, [isOpen, currentUser]);
  // Close instrument dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (instrumentDropdownRef.current && !instrumentDropdownRef.current.contains(event.target as Node)) {
        setIsInstrumentDropdownOpen(false);
        setSelectedInstrumentIndex(-1);
      }
    };
    if (isInstrumentDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isInstrumentDropdownOpen]);
  // Drag handlers - OPTIMIZED
  const handleDragStart = (e: React.MouseEvent) => {
    if (isMobile) return;
    // Only allow dragging from header
    if (!headerRef.current?.contains(e.target as Node)) return;
    // Don't start drag if clicking on close button
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX,
      y: e.clientY
    });
  };
  const handleDrag = (e: MouseEvent) => {
    if (!isDragging || !modalRef.current) return;
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    dragOffsetRef.current = {
      x: deltaX,
      y: deltaY
    };
    // Cancel previous RAF if still pending
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    // Schedule DOM update on next frame
    rafIdRef.current = requestAnimationFrame(() => {
      if (modalRef.current) {
        const finalTop = modalPosition.top + dragOffsetRef.current.y;
        const finalLeft = modalPosition.left + dragOffsetRef.current.x;
        modalRef.current.style.transform = `translate(${finalLeft - modalPosition.left}px, ${finalTop - modalPosition.top}px)`;
      }
    });
  };
  const handleDragEnd = () => {
    if (!isDragging) return;
    // Cancel any pending RAF
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    // Calculate new position with bounds checking
    const modalWidth = modalRef.current?.offsetWidth || 512;
    const modalHeight = modalRef.current?.offsetHeight || 600;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let newLeft = modalPosition.left + dragOffsetRef.current.x;
    let newTop = modalPosition.top + dragOffsetRef.current.y;
    // Constrain to viewport bounds
    newLeft = Math.max(0, Math.min(newLeft, viewportWidth - modalWidth));
    newTop = Math.max(0, Math.min(newTop, viewportHeight - modalHeight));
    // Update React state with final position
    setModalPosition({
      top: newTop,
      left: newLeft
    });
    // Reset transform
    if (modalRef.current) {
      modalRef.current.style.transform = '';
    }
    setIsDragging(false);
    dragOffsetRef.current = {
      x: 0,
      y: 0
    };
  };
  // Add global mouse event listeners for dragging - OPTIMIZED: Simplified dependencies
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
        // Cleanup RAF on unmount
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [isDragging, dragStartPos, modalPosition]);
  // Prevent text selection while dragging
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'move';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);
  // Lock background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Handle instrument input change and filter suggestions
  const handleInstrumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLessonForm(prev => ({
      ...prev,
      instrument: value
    }));
    // Filter instruments based on input
    if (value.trim()) {
      const filtered = allInstruments.filter(instrument => instrument.toLowerCase().includes(value.toLowerCase()));
      setFilteredInstruments(filtered);
      setIsInstrumentDropdownOpen(filtered.length > 0);
      setSelectedInstrumentIndex(-1);
    } else {
      setFilteredInstruments([]);
      setIsInstrumentDropdownOpen(false);
      setSelectedInstrumentIndex(-1);
    }
  };
  // Handle instrument selection from dropdown
  const handleSelectInstrument = (instrument: string) => {
    setLessonForm(prev => ({
      ...prev,
      instrument
    }));
    setIsInstrumentDropdownOpen(false);
    setSelectedInstrumentIndex(-1);
    setJustSelectedInstrument(true);
    // Blur the input to prevent focus from reopening dropdown
    if (instrumentInputRef.current) {
      instrumentInputRef.current.blur();
    }
    // Reset the flag after a short delay
    setTimeout(() => {
      setJustSelectedInstrument(false);
    }, 200);
  };
  // Handle keyboard navigation for instrument dropdown
  const handleInstrumentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isInstrumentDropdownOpen || filteredInstruments.length === 0) {
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedInstrumentIndex(prev => prev < filteredInstruments.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedInstrumentIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedInstrumentIndex >= 0) {
          handleSelectInstrument(filteredInstruments[selectedInstrumentIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsInstrumentDropdownOpen(false);
        setSelectedInstrumentIndex(-1);
        break;
    }
  };
  // Load students from Firestore
  const loadStudents = async () => {
    if (!currentUser) return;
    setLoadingStudents(true);
    try {
      // Fetch real students from Firebase using the teacherUtils function
      const fetchedStudents = await getTeacherStudents(currentUser.uid);
      // Map the students to the format expected by the dropdown
      const formattedStudents = fetchedStudents.map(student => ({
        id: student.studentId || student.id,
        name: student.name,
        instrument: student.instrument || '',
        email: student.email || ''
      }));
      setStudents(formattedStudents);
      console.log('Loaded students:', formattedStudents);
    } catch (err) {
      console.error('Error loading students:', err);
      setError('Failed to load students. Please try again.');
    } finally {
      setLoadingStudents(false);
    }
  };
  // Handle form input changes
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setLessonForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle precise time change with validation
  const handlePreciseTimeChange = value => {
    setLessonForm(prev => ({
      ...prev,
      preciseStartTime: value
    }));
    // Clear validation error when user changes time
    if (timeValidationError) {
      setTimeValidationError('');
    }
  };
  // Handle student selection change
  const handleStudentChange = (studentId: string) => {
    setLessonForm(prev => ({
      ...prev,
      studentId
    }));
    // Auto-populate instrument if a student is selected
    if (studentId) {
      const selectedStudent = students.find(s => s.id === studentId);
      if (selectedStudent && selectedStudent.instrument) {
        setLessonForm(prev => ({
          ...prev,
          instrument: selectedStudent.instrument
        }));
      }
    }
  };
  // Handle new student added from the modal
  const handleStudentAdded = newStudent => {
    // Add the new student to the students list
    const updatedStudents = [...students, {
      id: newStudent.studentId || newStudent.id,
      name: newStudent.name,
      instrument: newStudent.instrument || '',
      email: newStudent.email || ''
    }];
    setStudents(updatedStudents);
    // Select the new student in the dropdown
    setLessonForm(prev => ({
      ...prev,
      studentId: newStudent.studentId || newStudent.id,
      instrument: newStudent.instrument || prev.instrument
    }));
    // Close the add student modal
    setIsAddStudentModalOpen(false);
  };
  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault();
    if (!currentUser) {
      setError('You must be logged in to create a lesson');
      return;
    }
    // Helper to scroll form content to top on validation error
    const scrollToError = () => {
      formContentRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };
    // Validate form
    if (!lessonForm.studentId) {
      setError('Please select a student');
      scrollToError();
      return;
    }
    if (!lessonForm.date) {
      setError('Please select a date');
      scrollToError();
      return;
    }
    if (!lessonForm.preciseStartTime) {
      setError('Please select a start time');
      scrollToError();
      return;
    }
    if (isRecurring) {
      if (recurringDays.length === 0) {
        setError('Please select at least one day for recurring lessons');
        scrollToError();
        return;
      }
      if (!recurringEndDate) {
        setError('End date is required for recurring lessons');
        scrollToError();
        return;
      }
      if (calculateRecurringCount() === 0) {
        setError('No lessons will be created with these settings');
        scrollToError();
        return;
      }
      if (calculateRecurringCount() > 365) {
        toast.error('Maximum 365 lessons allowed in a recurring series');
        return;
      }
    }
    setSubmitting(true);
    setError('');
    setTimeValidationError('');
    try {
      // Find the selected student to get their name
      const selectedStudent = students.find(s => s.id === lessonForm.studentId);
      if (!selectedStudent) {
        throw new Error('Selected student not found');
      }
      // Parse the date and time strings properly
      // Format: date = "YYYY-MM-DD", time = "HH:MM" (24h) or "HH:MM AM/PM"
      const time24h = lessonForm.preciseStartTime;
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(time24h)) {
        setTimeValidationError('Invalid time format. Please select a valid time.');
        return;
      }
      // Parse date - ensure we're using the EXACT date from the form
      const dateParts = lessonForm.date.split('-');
      console.log('[CALENDAR] Date parts after split:', dateParts);
      if (dateParts.length !== 3) {
        throw new Error(`Invalid date format: ${lessonForm.date}. Expected YYYY-MM-DD`);
      }
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]);
      const day = parseInt(dateParts[2]);
      console.log('[CALENDAR] Parsed date components:', {
        year,
        month,
        day
      });
      // Validate parsed values
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        throw new Error(`Invalid date values: year=${year}, month=${month}, day=${day}`);
      }
      if (month < 1 || month > 12) {
        throw new Error(`Invalid month: ${month}. Must be 1-12`);
      }
      if (day < 1 || day > 31) {
        throw new Error(`Invalid day: ${day}. Must be 1-31`);
      }
      // Parse time - already validated above
      const timeParts = time24h.split(':');
      if (timeParts.length !== 2) {
        throw new Error(`Invalid time format: ${time24h}`);
      }
      const hours = parseInt(timeParts[0]);
      const minutes = parseInt(timeParts[1]);
      console.log('[CALENDAR] Parsed time components:', {
        hours,
        minutes
      });
      if (isNaN(hours) || isNaN(minutes)) {
        throw new Error(`Invalid time values: hours=${hours}, minutes=${minutes}`);
      }
      // NEW: Additional validation for time values
      if (hours < 0 || hours > 23) {
        setTimeValidationError('Invalid hour. Must be 0-23.');
        setSubmitting(false);
        return;
      }
      if (minutes < 0 || minutes > 59) {
        setTimeValidationError('Invalid minutes. Must be 0-59.');
        setSubmitting(false);
        return;
      }
      // Create Date object using EXACT values from form
      // IMPORTANT: JavaScript Date months are 0-indexed (0=January, 11=December)
      // So we subtract 1 from the month value
      const startAt = new Date(year, month - 1, day, hours, minutes, 0, 0);
      console.log('[CALENDAR] Created Date object:', {
        input: {
          year,
          month,
          day,
          hours,
          minutes
        },
        dateObject: startAt,
        iso: startAt.toISOString(),
        local: startAt.toLocaleString(),
        getFullYear: startAt.getFullYear(),
        getMonth: startAt.getMonth(),
        getDate: startAt.getDate(),
        getHours: startAt.getHours(),
        getMinutes: startAt.getMinutes()
      });
      // Verify the Date object has the correct values
      if (startAt.getFullYear() !== year) {
        console.error('[CALENDAR] Year mismatch!', {
          expected: year,
          actual: startAt.getFullYear()
        });
      }
      if (startAt.getMonth() !== month - 1) {
        console.error('[CALENDAR] Month mismatch!', {
          expected: month - 1,
          actual: startAt.getMonth()
        });
      }
      if (startAt.getDate() !== day) {
        console.error('[CALENDAR] Day mismatch!', {
          expected: day,
          actual: startAt.getDate()
        });
      }
      // Get timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // NEW: Handle recurring lessons
      if (isRecurring) {
        console.log('[RECURRING] Creating recurring lessons');
        // Calculate lesson dates
        const lessonDates: Date[] = [];
        const today = new Date(startAt);
        today.setHours(0, 0, 0, 0);
        const end = new Date(recurringEndDate + 'T23:59:59');
        const cursor = new Date(today);
        while (cursor <= end) {
          if (recurringDays.includes(cursor.getDay())) {
            // Create a new date for this specific day, keeping the original time
            const lessonDate = new Date(cursor);
            lessonDate.setHours(hours, minutes, 0, 0);
            lessonDates.push(lessonDate);
          }
          cursor.setDate(cursor.getDate() + 1);
        }
        console.log('[RECURRING] Generated lesson dates:', {
          count: lessonDates.length,
          first: lessonDates[0]?.toISOString(),
          last: lessonDates[lessonDates.length - 1]?.toISOString()
        });
        // Generate shared recurringGroupId
        const recurringGroupId = crypto.randomUUID();
        // Use writeBatch for atomic creation
        const batch = writeBatch(db);
        const teacherRef = firestoreDoc(db, 'teachers', currentUser.uid);
        const calendarCol = collection(teacherRef, 'calendar');
        lessonDates.forEach((lessonDate, index) => {
          const lessonEndAt = new Date(lessonDate.getTime() + parseInt(lessonForm.duration.toString()) * 60000);
          const payload = {
            teacherId: currentUser.uid,
            studentId: lessonForm.studentId,
            studentName: selectedStudent.name,
            title: lessonForm.title || `${lessonForm.instrument || 'Music'} Lesson`,
            startAt: Timestamp.fromDate(lessonDate),
            endAt: Timestamp.fromDate(lessonEndAt),
            durationMinutes: parseInt(lessonForm.duration.toString()),
            location: lessonForm.location,
            instrument: lessonForm.instrument,
            notes: lessonForm.notes,
            timezone: timezone,
            lessonType: null,
            status: 'SCHEDULED',
            cancelled: false,
            recurringGroupId: recurringGroupId,
            recurringIndex: index,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };
          const newDocRef = firestoreDoc(calendarCol);
          batch.set(newDocRef, payload);
        });
        // Commit batch
        await batch.commit();
        console.log('[RECURRING] ✅ Batch commit successful!');
        // Show success message
        toast.success(`${lessonDates.length} recurring lessons created successfully!`);
        // Dispatch custom event
        try {
          window.dispatchEvent(new CustomEvent('lessonCreated', {
            detail: {
              recurring: true,
              count: lessonDates.length,
              groupId: recurringGroupId
            }
          }));
        } catch (eventError) {
          console.warn('[RECURRING] Error dispatching event:', eventError);
        }
        // Call the onLessonAdded callback
        if (onLessonAdded) {
          try {
            onLessonAdded({
              recurring: true,
              count: lessonDates.length,
              groupId: recurringGroupId
            });
          } catch (callbackError) {
            console.error('[RECURRING] Error in onLessonAdded:', callbackError);
          }
        }
      } else {
        // Create single lesson
        // Prepare lesson input for service
        const lessonInput: CreateLessonInput = {
          title: lessonForm.title || `${lessonForm.instrument || 'Music'} Lesson`,
          studentId: lessonForm.studentId,
          studentName: selectedStudent.name,
          teacherId: currentUser.uid,
          startAt: startAt,
          durationMin: parseInt(lessonForm.duration.toString()),
          location: lessonForm.location,
          instrument: lessonForm.instrument,
          notes: lessonForm.notes,
          timezone: timezone
        };
        console.log('[CALENDAR] Final lesson input:', {
          ...lessonInput,
          startAt: startAt.toISOString(),
          startAtLocal: startAt.toLocaleString()
        });
        // Call the service to create the lesson
        const docId = await createLesson(lessonInput);
        console.log('[CALENDAR] Lesson created successfully with ID:', docId);
        // Show success message FIRST (before any callbacks that might fail)
        toast.success('Lesson created successfully!');
        // Dispatch custom event (wrap in try-catch to prevent errors from affecting success flow)
        try {
          window.dispatchEvent(new CustomEvent('lessonCreated', {
            detail: {
              id: docId,
              ...lessonInput
            }
          }));
        } catch (eventError) {
          console.warn('[CALENDAR] Error dispatching event:', eventError);
        }
        // Call the onLessonAdded callback (wrap in try-catch to prevent errors from affecting success flow)
        if (onLessonAdded) {
          try {
            onLessonAdded({
              id: docId,
              ...lessonInput
            });
          } catch (callbackError) {
            console.warn('[CALENDAR] Error in onLessonAdded callback:', callbackError);
          }
        }
        // Close the modal
        onClose();
      }
    } catch (err) {
      console.error('[CALENDAR] Error creating lesson:', err);
      // Only show error if the actual creation failed
      setError(err.message || 'Failed to create lesson. Please try again.');
      toast.error(err.message || 'Failed to create lesson. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className={`fixed inset-0 z-50 ${isMobile ? 'flex flex-col' : 'overflow-y-auto'}`} data-id="element-969">
      <div className={`${isMobile ? 'flex-1 flex items-center justify-center px-4 py-4' : 'flex items-center justify-center min-h-screen px-4'}`} data-id="element-970">
        {/* Background overlay - darkened without blur */}
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-971">
          <div className={`absolute inset-0 ${darkMode ? 'bg-black' : 'bg-gray-900'} opacity-75`} data-id="element-972"></div>
        </div>
        {/* Modal panel */}
        <div ref={modalRef} className={`${isMobile ? 'relative w-full' : 'fixed'} rounded-xl text-left transform ${isDragging ? 'transition-none' : 'transition-all'} max-w-lg w-full ${darkMode ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50 shadow-2xl' : 'bg-white border border-gray-200 shadow-xl'} ${isDragging ? 'cursor-move' : ''} flex flex-col`} style={{
        maxHeight: isMobile ? 'calc(100dvh - 2rem)' : '88vh',
        ...(isMobile ? {} : {
          top: `${modalPosition.top}px`,
          left: `${modalPosition.left}px`,
          userSelect: isDragging ? 'none' : 'auto',
          boxShadow: darkMode ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)' : undefined
        })
      }} data-id="element-973">
          {/* Header - with drag handle */}
          <div ref={headerRef} className={`px-6 py-5 flex items-center justify-between border-b ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} ${!isMobile ? 'cursor-move' : ''} flex-shrink-0`} onMouseDown={handleDragStart} data-id="element-974">
            <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-975">
              Create New Lesson
            </h3>
            <button onClick={onClose} className={`rounded-lg ${darkMode ? 'hover:bg-navy-700/50' : 'hover:bg-gray-100'} p-1.5 transition-colors`} data-id="element-976">
              <XIcon className="h-5 w-5" data-id="element-977" />
            </button>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0" data-id="element-978">
            <div ref={formContentRef} className="p-6 space-y-5 overflow-y-auto flex-grow" data-id="element-979">
              {/* Error message */}
              {error && <div className={`${darkMode ? 'bg-red-500/20 text-red-200 border-red-500/30' : 'bg-red-50 text-red-700 border-red-200'} px-4 py-2 rounded-lg text-sm border`} data-id="element-980">
                  {error}
                </div>}
              {/* Title */}
              <div data-id="element-981">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-982">
                  Lesson Title
                </label>
                <input type="text" name="title" value={lessonForm.title} onChange={handleInputChange} placeholder="Add lesson title" className={`w-full px-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all`} data-id="element-983" />
              </div>
              {/* Student - REPLACED with StudentPicker */}
              <div className="overflow-visible" data-id="element-984">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-985">
                  Student<span className="text-red-500 ml-0.5" data-id="element-986">*</span>
                </label>
                <div className={`${!lessonForm.studentId && error ? 'ring-2 ring-red-500 rounded-lg' : ''}`} data-id="element-987">
                  <StudentPicker value={lessonForm.studentId} onChange={handleStudentChange} onAddStudent={handleStudentAdded} students={students.map(student => ({
                  id: student.id,
                  name: student.name,
                  email: student.email || '',
                  instrument: student.instrument || ''
                }))} darkMode={darkMode} loading={loadingStudents} required={true} data-id="element-988" />
                </div>
              </div>
              {/* NEW: Date Input Field */}
              <div data-id="element-989">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-990">
                  Date
                </label>
                <div className="relative" data-id="element-991">
                  <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-992">
                    <CalendarIcon className="h-4 w-4 text-[#3B82F6]" data-id="element-993" />
                  </div>
                  <input type="date" name="date" value={lessonForm.date} onChange={handleInputChange} required className={`w-full max-w-full min-w-0 pl-11 pr-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all md:min-w-[auto]`} style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                  colorScheme: darkMode ? 'dark' : 'light'
                }} data-id="element-994" />
                </div>
              </div>

              {/* NEW: Repeat Toggle Section */}
              <div className={`border-t ${darkMode ? 'border-navy-700/30' : 'border-gray-200'} pt-5`} data-id="element-995">
                <div className="flex items-center justify-between mb-3" data-id="element-996">
                  <div className="flex items-center space-x-2" data-id="element-997">
                    <RepeatIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-998" />
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-999">
                      Repeat this lesson
                    </label>
                  </div>
                  <button type="button" onClick={() => setIsRecurring(!isRecurring)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isRecurring ? 'bg-[#10B981]' : darkMode ? 'bg-navy-700' : 'bg-gray-300'}`} data-id="element-1000">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isRecurring ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1001" />
                  </button>
                </div>

                {/* End Condition UI (shown when recurring is enabled) */}
                {isRecurring && <div className={`mt-4 p-4 rounded-lg border ${darkMode ? 'bg-navy-900/30 border-navy-700/50' : 'bg-gray-50 border-gray-200'} space-y-4`} data-id="element-1002">
                    <div data-id="element-1003">
                      <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`} data-id="element-1004">
                        Repeat on days
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-1005">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => <button key={day} type="button" onClick={() => {
                      if (recurringDays.includes(index)) {
                        setRecurringDays(recurringDays.filter(d => d !== index));
                      } else {
                        setRecurringDays([...recurringDays, index]);
                      }
                    }} className={`w-10 h-10 rounded-full text-sm font-medium flex items-center justify-center transition-colors ${recurringDays.includes(index) ? 'bg-[#10B981] text-white' : darkMode ? 'bg-navy-800 text-gray-300 hover:bg-navy-700' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`} data-id="element-1006">
                              {day[0]}
                            </button>)}
                      </div>
                    </div>

                    <div data-id="element-1007">
                      <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`} data-id="element-1008">
                        End date
                      </label>
                      <input type="date" value={recurringEndDate} onChange={e => {
                    if (e.target.value) {
                      setRecurringEndDate(e.target.value);
                    } else {
                      // iOS native "Reset" clears value — restore default (4 weeks ahead)
                      const defaultEnd = new Date(lessonForm.date || new Date());
                      defaultEnd.setDate(defaultEnd.getDate() + 28);
                      setRecurringEndDate(defaultEnd.toISOString().split('T')[0]);
                    }
                  }} min={lessonForm.date || new Date().toISOString().split('T')[0]} className={`w-full rounded-lg border ${darkMode ? 'bg-navy-800 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} px-3 py-2 text-sm focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20`} style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield',
                    colorScheme: darkMode ? 'dark' : 'light'
                  }} data-id="element-1009" />
                    </div>

                    <div className={`text-sm font-medium flex items-center ${darkMode ? 'text-[#10B981]' : 'text-[#10B981]'}`} data-id="element-1010">
                      <RepeatIcon className="h-4 w-4 mr-1.5" data-id="element-1011" />
                      Will create {calculateRecurringCount()} lessons through{' '}
                      {new Date(recurringEndDate + 'T12:00:00').toLocaleDateString()}
                    </div>
                  </div>}
              </div>

              {/* Duration and Time Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1012">
                {/* Duration */}
                <div data-id="element-1013">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1014">
                    Duration
                  </label>
                  <DurationPickerDropdown value={lessonForm.duration} onChange={value => setLessonForm(prev => ({
                  ...prev,
                  duration: value
                }))} darkMode={darkMode} required={true} data-id="element-1015" />
                </div>
                {/* Time */}
                <div data-id="element-1016">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1017">
                    Start time
                  </label>
                  <TimePickerDropdown value={lessonForm.preciseStartTime} onChange={handlePreciseTimeChange} darkMode={darkMode} required={true} error={!!timeValidationError} data-id="element-1018" />
                  {timeValidationError && <p className="mt-1.5 text-sm text-red-400 flex items-center" data-id="element-1019">
                      <span className="mr-1" data-id="element-1020">⚠</span>
                      {timeValidationError}
                    </p>}
                </div>
              </div>
              {/* Location and Instrument Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1021">
                {/* Location */}
                <div data-id="element-1022">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1023">
                    Location
                  </label>
                  <CustomDropdown value={lessonForm.location} options={locationOptions} onChange={value => handleInputChange({
                  target: {
                    name: 'location',
                    value
                  }
                })} darkMode={darkMode} icon={<MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-1025" />} required={true} data-id="element-1024" />
                </div>
                {/* Instrument with Autosuggest */}
                <div className="overflow-visible" data-id="element-1026">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1027">
                    Instrument
                  </label>
                  <div className="relative overflow-visible" ref={instrumentDropdownRef} data-id="element-1028">
                    <div className="relative" data-id="element-1029">
                      <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-1030">
                        <MusicIcon className="h-4 w-4 text-[#81C784]" data-id="element-1031" />
                      </div>
                      <input ref={instrumentInputRef} type="text" name="instrument" value={lessonForm.instrument} onChange={handleInstrumentChange} onKeyDown={handleInstrumentKeyDown} onFocus={() => {
                      // Don't open if we just selected an instrument
                      if (justSelectedInstrument) {
                        return;
                      }
                      if (lessonForm.instrument.trim()) {
                        const filtered = allInstruments.filter(instrument => instrument.toLowerCase().includes(lessonForm.instrument.toLowerCase()));
                        setFilteredInstruments(filtered);
                        setIsInstrumentDropdownOpen(filtered.length > 0);
                      }
                    }} placeholder="Enter instrument" className={`w-full pl-11 pr-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all`} data-id="element-1032" />
                    </div>
                    {/* Autosuggest Dropdown */}
                    {isInstrumentDropdownOpen && filteredInstruments.length > 0 && <div className={`absolute z-[9999] w-full mt-1 rounded-lg shadow-lg ${darkMode ? 'bg-navy-800 border border-navy-700/50' : 'bg-white border border-gray-200'} max-h-60 overflow-auto`} style={{
                    boxShadow: darkMode ? '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)' : '0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)'
                  }} data-id="element-1033">
                          <ul className="py-1" data-id="element-1034">
                            {filteredInstruments.map((instrument, index) => <li key={instrument} className={`px-4 py-2.5 cursor-pointer flex items-center ${index === selectedInstrumentIndex ? darkMode ? 'bg-navy-700/50' : 'bg-gray-100' : darkMode ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} ${darkMode ? 'text-white' : 'text-gray-900'} transition-colors`} onClick={() => handleSelectInstrument(instrument)} onMouseEnter={() => setSelectedInstrumentIndex(index)} data-id="element-1035">
                                <MusicIcon className="h-4 w-4 text-gray-400 mr-2.5 flex-shrink-0" data-id="element-1036" />
                                <span className="text-sm" data-id="element-1037">{instrument}</span>
                              </li>)}
                          </ul>
                        </div>}
                  </div>
                </div>
              </div>
              {/* Notes */}
              <div data-id="element-1038">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-1039">
                  Notes
                </label>
                <div className="relative" data-id="element-1040">
                  <div className="absolute top-3.5 left-3.5 flex items-start pointer-events-none" data-id="element-1041">
                    <FileTextIcon className="h-4 w-4 text-[#10B981]" data-id="element-1042" />
                  </div>
                  <textarea name="notes" value={lessonForm.notes} onChange={handleInputChange} rows={3} placeholder="Any notes about this lesson..." className={`w-full pl-11 pr-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all`} data-id="element-1043" />
                </div>
              </div>
            </div>
            {/* Footer with actions */}
            <div className={`px-6 py-4 flex justify-end space-x-3 border-t ${darkMode ? 'border-navy-700/30 bg-navy-900/20' : 'border-gray-200 bg-gray-50'} flex-shrink-0`} data-id="element-1044">
              <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-navy-700/50 text-white hover:bg-navy-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`} disabled={submitting} data-id="element-1045">
                Cancel
              </button>
              <button type="submit" className={`px-4 py-2 rounded-md border-2 text-sm font-medium transition-colors flex items-center ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} disabled={submitting} data-id="element-1046">
                {submitting ? <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1047">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1048"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1049"></path>
                    </svg>
                    Creating...
                  </> : 'Create Lesson'}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Add Student Modal - NOW RENDERED */}
      <AddStudentModal isOpen={isAddStudentModalOpen} onClose={() => setIsAddStudentModalOpen(false)} onStudentAdded={handleStudentAdded} darkMode={darkMode} data-id="element-1050" />
      {/* Add styles to fix time input visibility and focus issues */}
      <style jsx global data-id="element-1051">{`
        /* Hide browser default calendar and time picker icons */
        .custom-date-input::-webkit-calendar-picker-indicator,
        .custom-time-input::-webkit-calendar-picker-indicator {
          opacity: 0;
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          cursor: pointer;
        }

        /* Duration picker scrollbar styling to match Start Time picker */
        ul::-webkit-scrollbar {
          width: 6px;
        }
        ul::-webkit-scrollbar-track {
          background: transparent;
        }
        ul::-webkit-scrollbar-thumb {
          background-color: ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
          border-radius: 3px;
        }
        ul::-webkit-scrollbar-thumb:hover {
          background-color: ${darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
        }

        /* Mobile-only: Normalize all input field heights to match Date field */
        @media (max-width: 767px) {
          /* Normalize all text-based inputs */
          input[type='text'],
          input[type='email'],
          input[type='tel'],
          input[type='date'] {
            padding-top: 0.625rem !important;
            padding-bottom: 0.625rem !important;
          }
        }
      `}</style>
    </div>;
};